// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

export type SwordDrillMode = 'full-bible' | 'new-testament' | 'old-testament' | 'thematic'

export type SwordDrillPhase = 'setup' | 'themeQuestion' | 'verse'

export interface SwordDrillTheme {
  answer: string
  name: string
  question: string
  verses: SwordDrillThemeVerse[]
}

export interface SwordDrillThemeVerse {
  reference: string
  text: string
}
export interface SwordDrillVerse {
  reference: string
  testament: 'new' | 'old'
  text: string
}

// In thematic mode, each round draws 6 verses from a randomly chosen theme,
// shows them sequentially, then asks the theme question. Above a theme's
// minimum-of-6 the unused verses are simply discarded for that round; the
// next round of the same theme (if it ever recurs) reshuffles.
export const VERSES_PER_THEME_ROUND = 6

export interface SwordDrillSyncState {
  config: {
    mode: SwordDrillMode
  }
  // Theme metadata when in thematic mode and currently on a theme. Null
  // otherwise. Held separately from currentVerse so the UI can show the
  // question/answer banner while the verse is hidden.
  currentTheme: null | { answer: string, name: string, question: string }
  // The active queue of verses for the current run. For Bible/OT/NT modes
  // this is a flat verse queue; for thematic mode it's the 6 verses of the
  // current theme, plus the theme metadata. The leader advances through this
  // list one tap at a time.
  currentVerse: null | SwordDrillThemeVerse | SwordDrillVerse
  phase: SwordDrillPhase
  revealed: boolean
  // 1-indexed position within the current theme (1..VERSES_PER_THEME_ROUND).
  // Null in non-thematic modes; null in thematic mode while between themes.
  themeProgress: null | number
}

// ──────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────

function cloneForSync<T>(value: T): T {
  const rawValue = toRaw(value) as unknown

  if (rawValue === null || typeof rawValue !== 'object') {
    return rawValue as T
  }

  if (Array.isArray(rawValue)) {
    return rawValue.map(item => cloneForSync(item)) as T
  }

  const cloned: Record<string, unknown> = {}
  for (const [ key, nestedValue ] of Object.entries(rawValue as Record<string, unknown>)) {
    cloned[key] = cloneForSync(nestedValue)
  }
  return cloned as T
}

function shuffle<T>(input: T[]): T[] {
  const copy = [ ...input ]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[ copy[i], copy[j] ] = [ copy[j]!, copy[i]! ]
  }
  return copy
}

// ──────────────────────────────────────────────────────────
// Store
// ──────────────────────────────────────────────────────────

export const useSwordDrillStore = defineStore('swordDrill', () => {
  // ── Phase ──────────────────────────────────────────────
  const phase = ref<SwordDrillPhase>('setup')

  // ── Loaded content (set by the page after queryCollection) ──
  const verses = ref<SwordDrillVerse[]>([])
  const themes = ref<SwordDrillTheme[]>([])

  // ── Setup config ───────────────────────────────────────
  const config = ref<{ mode: SwordDrillMode }>({
    mode: 'full-bible',
  })

  // ── Run state ──────────────────────────────────────────
  const currentVerse = ref<null | SwordDrillThemeVerse | SwordDrillVerse>(null)
  const currentTheme = ref<null | { answer: string, name: string, question: string }>(null)
  const themeProgress = ref<null | number>(null)
  const revealed = ref(false)

  // Pre-shuffled queues so we deal verses without immediate repeats. Not
  // synced — both controller and presenter rebuild from the same content.
  // For thematic mode, the live theme's 6 verses live in `themeVerseQueue`;
  // for Bible/OT/NT it's `verseQueue`.
  const verseQueue = ref<SwordDrillVerse[]>([])
  const themeQueue = ref<SwordDrillTheme[]>([])
  const themeVerseQueue = ref<SwordDrillThemeVerse[]>([])

  // ── Setters used by the page after loading content ────
  function setVerses(list: SwordDrillVerse[]) {
    verses.value = list
  }
  function setThemes(list: SwordDrillTheme[]) {
    themes.value = list
  }

  // ── Game lifecycle ─────────────────────────────────────

  function startGame() {
    revealed.value = false
    currentVerse.value = null
    currentTheme.value = null
    themeProgress.value = null
    themeVerseQueue.value = []

    if (config.value.mode === 'thematic') {
      themeQueue.value = shuffle(themes.value)
      verseQueue.value = []
      advanceToNextTheme()
    } else {
      themeQueue.value = []
      verseQueue.value = shuffle(filteredVerses())
      advanceToNextVerse()
    }
  }

  function exitGame() {
    phase.value = 'setup'
    revealed.value = false
    currentVerse.value = null
    currentTheme.value = null
    themeProgress.value = null
    verseQueue.value = []
    themeQueue.value = []
    themeVerseQueue.value = []
  }

  // ── Per-mode pool selection ────────────────────────────
  function filteredVerses(): SwordDrillVerse[] {
    const mode = config.value.mode
    if (mode === 'old-testament') {
      return verses.value.filter(v => v.testament === 'old')
    }
    if (mode === 'new-testament') {
      return verses.value.filter(v => v.testament === 'new')
    }
    return verses.value
  }

  // ── Advance helpers ────────────────────────────────────

  function refillVerseQueue() {
    const pool = filteredVerses()
    if (pool.length === 0) {
      verseQueue.value = []
      return
    }
    // Avoid boundary repeat: if the next reshuffle leads with the verse just
    // shown, swap it with the second entry so the kids never see the same
    // reference twice in a row across cycles.
    const last = currentVerse.value && 'testament' in currentVerse.value
      ? currentVerse.value.reference
      : null
    const nextDeck = shuffle(pool)
    if (last !== null && nextDeck.length > 1 && nextDeck[0]!.reference === last) {
      ;[ nextDeck[0], nextDeck[1] ] = [ nextDeck[1]!, nextDeck[0]! ]
    }
    verseQueue.value = nextDeck
  }

  function refillThemeQueue() {
    if (themes.value.length === 0) {
      themeQueue.value = []
      return
    }
    const last = currentTheme.value?.name ?? null
    const nextDeck = shuffle(themes.value)
    if (last !== null && nextDeck.length > 1 && nextDeck[0]!.name === last) {
      ;[ nextDeck[0], nextDeck[1] ] = [ nextDeck[1]!, nextDeck[0]! ]
    }
    themeQueue.value = nextDeck
  }

  function advanceToNextVerse() {
    if (verseQueue.value.length === 0) {
      refillVerseQueue()
    }
    const next = verseQueue.value.shift() ?? null
    currentVerse.value = next
    revealed.value = false
    phase.value = next ? 'verse' : 'setup'
  }

  function advanceToNextTheme() {
    if (themeQueue.value.length === 0) {
      refillThemeQueue()
    }
    const theme = themeQueue.value.shift() ?? null
    if (!theme) {
      // No themes loaded; fall back to setup.
      currentTheme.value = null
      currentVerse.value = null
      phase.value = 'setup'
      return
    }
    currentTheme.value = {
      answer: theme.answer,
      name: theme.name,
      question: theme.question,
    }
    // Pick 6 random verses from the theme. If the theme has fewer than 6
    // (shouldn't happen given content rules, but defend anyway), use what's
    // there.
    const slice = shuffle(theme.verses).slice(0, Math.min(VERSES_PER_THEME_ROUND, theme.verses.length))
    themeVerseQueue.value = slice
    themeProgress.value = 0
    advanceWithinTheme()
  }

  function advanceWithinTheme() {
    const next = themeVerseQueue.value.shift() ?? null
    if (next) {
      currentVerse.value = next
      themeProgress.value = (themeProgress.value ?? 0) + 1
      revealed.value = false
      phase.value = 'verse'
      return
    }
    // All verses in this theme have been served; move to the question.
    currentVerse.value = null
    revealed.value = false
    phase.value = 'themeQuestion'
  }

  // ── Public actions ─────────────────────────────────────

  function reveal() {
    revealed.value = true
  }

  // The leader's "next" button. Behavior depends on phase + mode.
  function next() {
    if (phase.value === 'setup') {
      return
    }
    if (config.value.mode !== 'thematic') {
      advanceToNextVerse()
      return
    }
    if (phase.value === 'themeQuestion') {
      // Done with this theme — pick a new one and start again.
      advanceToNextTheme()
      return
    }
    // Mid-theme: serve the next verse, or move to the question if we've
    // exhausted the 6.
    advanceWithinTheme()
  }

  // ── Sync state ─────────────────────────────────────────

  function getSyncState(): SwordDrillSyncState {
    return {
      config: cloneForSync(config.value),
      currentTheme: currentTheme.value ? cloneForSync(currentTheme.value) : null,
      currentVerse: currentVerse.value ? cloneForSync(currentVerse.value) : null,
      phase: phase.value,
      revealed: revealed.value,
      themeProgress: themeProgress.value,
    }
  }

  function applySyncState(next: SwordDrillSyncState) {
    config.value = cloneForSync(next.config)
    currentTheme.value = next.currentTheme ? cloneForSync(next.currentTheme) : null
    currentVerse.value = next.currentVerse ? cloneForSync(next.currentVerse) : null
    phase.value = next.phase
    revealed.value = next.revealed
    themeProgress.value = next.themeProgress
    // Queues are not synced (presenter is read-only); leave whatever's there.
  }

  return {
    advanceToNextTheme,
    advanceToNextVerse,
    advanceWithinTheme,
    applySyncState,
    config,
    currentTheme,
    currentVerse,
    exitGame,
    getSyncState,
    next,
    phase,
    reveal,
    revealed,
    setThemes,
    setVerses,
    startGame,
    themeProgress,
    themes,
    verses,
  }
})
